import React from "react";
import { Link } from "react-router-dom";
import { Handle, Position } from "@xyflow/react";
import { ChevronDown, ChevronRight, Flag, Users } from "lucide-react";
import { templatePath } from "../Templates/templateUrls";

// Gives custom nodes the block toggle handler without threading it through
// node data. Value is `onToggleBlock(blockKey)`.
export const BlockToggleContext = React.createContext(null);

const RESOURCE_STYLES = {
  soap_note_template: { label: "NOTE", className: "border-blue-200 bg-blue-50 text-blue-700" },
  conversation_prompt: { label: "CONV", className: "border-green-200 bg-green-50 text-green-700" },
  secondary_prompt: { label: "2ND", className: "border-purple-200 bg-purple-50 text-purple-700" },
};

// Legend mapping each resource badge to its full template name.
const RESOURCE_LEGEND = [
  { key: "soap_note_template", name: "SOAP Note Template" },
  { key: "conversation_prompt", name: "Conversation Prompt Template" },
  { key: "secondary_prompt", name: "Secondary Prompt Template" },
];

export function ResourceLegend({ vertical = false } = {}) {
  return (
    <div
      className={
        vertical
          ? "flex flex-col gap-1"
          : "flex flex-wrap items-center gap-x-4 gap-y-1"
      }
    >
      {RESOURCE_LEGEND.map(({ key, name }) => {
        const meta = RESOURCE_STYLES[key];
        return (
          <span key={key} className="flex items-center gap-1">
            <span
              className={`shrink-0 rounded border px-1 py-px text-[8px] font-semibold ${meta.className}`}
            >
              {meta.label}
            </span>
            <span className="text-[10px] text-gray-600">{name}</span>
          </span>
        );
      })}
    </div>
  );
}

// Same template links as the table; nodrag/nopan so a click doesn't pan the
// canvas or select the node.
export function ResourceLinks({ resources }) {
  if (!resources?.length) return null;
  return (
    <div className="mt-2 space-y-1">
      {resources.map(({ key, id }) => {
        const meta = RESOURCE_STYLES[key] || { label: key, className: "border-gray-200 bg-gray-50 text-gray-600" };
        return (
          <div key={key} className="flex items-center gap-1.5 min-w-0">
            <span
              className={`shrink-0 rounded border px-1 py-0.5 text-[9px] font-semibold ${meta.className}`}
            >
              {meta.label}
            </span>
            <Link
              to={templatePath(id)}
              className="nodrag nopan truncate text-[11px] text-ktx-purple underline decoration-ktx-purple/40 underline-offset-2 transition-colors hover:decoration-ktx-purple"
              title={`Find ${id} in Templates`}
              onClick={(e) => e.stopPropagation()}
              onMouseDown={(e) => e.stopPropagation()}
            >
              {id}
            </Link>
          </div>
        );
      })}
    </div>
  );
}

export function SpecialtyNode({ data }) {
  return (
    <div className="rounded-lg border-2 border-purple2 bg-purple2 px-4 py-3 text-white shadow-md">
      <div className="text-sm font-bold leading-tight">{data.specialty}</div>
      <div className="mt-0.5 text-[11px] opacity-80">{data.routeCount} routes</div>
      <Handle id="bottom" type="source" position={Position.Bottom} className="!bg-gray-400" />
      <Handle id="right" type="source" position={Position.Right} className="!bg-indigo-500" />
    </div>
  );
}

// Rule 10: highlighted fallback box, adjacent to the specialty root.
export function DefaultNode({ data }) {
  return (
    <div className="rounded-lg border-[3px] border-indigo-500 bg-indigo-50 px-4 py-3 shadow-[0_0_0_4px_rgba(99,102,241,0.25)]">
      <div className="flex items-center gap-1.5">
        <span className="text-xs font-bold uppercase tracking-wide text-indigo-800">
          Fallback template
        </span>
      </div>
      <ResourceLinks resources={data.resources} />
      <Handle id="left" type="target" position={Position.Left} className="!bg-indigo-500" />
    </div>
  );
}

// Top-layer block: visit type(s), "Any visit type", or a flag-only block.
export function GroupNode({ id, data }) {
  const isFlag = data.kind === "flag";
  const isAny = data.kind === "any";
  const onToggle = React.useContext(BlockToggleContext);
  return (
    <div
      className={`cursor-pointer rounded-lg border-2 px-4 py-2.5 shadow-sm transition-colors ${
        data.highlighted ? "ring-2 ring-ktx-purple ring-offset-1 " : ""
      }${
        isFlag
          ? "border-orange-300 bg-orange-50 hover:bg-orange-100"
          : isAny
          ? "border-gray-300 bg-gray-50 hover:bg-gray-100"
          : "border-sky-300 bg-sky-50 hover:bg-sky-100"
      }`}
      title="Click to expand/collapse"
    >
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          aria-expanded={data.expanded}
          aria-label={`${data.expanded ? "Collapse" : "Expand"} ${data.label}`}
          className="nodrag -m-1 cursor-pointer rounded p-1 hover:bg-black/5 focus:outline-none focus:ring-2 focus:ring-ktx-purple"
          onClick={(event) => {
            // Stop ReactFlow's onNodeClick from also firing (it would toggle a
            // second time and cancel this one out).
            event.stopPropagation();
            onToggle?.(id);
          }}
        >
          {data.expanded ? (
            <ChevronDown className="h-3.5 w-3.5 shrink-0 text-gray-500" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 shrink-0 text-gray-500" />
          )}
        </button>
        {isFlag && <Flag className="h-3.5 w-3.5 shrink-0 text-orange-500" />}
        <span
          className={`text-xs font-bold leading-tight ${
            isFlag ? "text-orange-800" : isAny ? "text-gray-600" : "text-sky-800"
          }`}
        >
          {data.label}
        </span>
      </div>
      <div className="mt-1 text-[10px] text-gray-500">
        {data.routeCount} route{data.routeCount !== 1 ? "s" : ""}
        {data.providerCount > 0 && (
          <>
            {" · "}
            {data.providerCount} provider{data.providerCount !== 1 ? "s" : ""}
          </>
        )}
      </div>
      <Handle type="target" position={Position.Top} className="!bg-gray-400" />
      <Handle type="source" position={Position.Bottom} className="!bg-gray-400" />
    </div>
  );
}

// Intermediate flag node inside a visit-type branch (rules 4 & 7).
export function FlagNode({ data }) {
  return (
    <div className="rounded-lg border-2 border-dashed border-orange-300 bg-orange-50 px-3 py-2">
      <div className="flex items-center gap-1.5">
        <Flag className="h-3.5 w-3.5 shrink-0 text-orange-500" />
        <span className="text-[11px] font-semibold text-orange-800">{data.label}</span>
      </div>
      <Handle type="target" position={Position.Top} className="!bg-gray-400" />
      <Handle type="source" position={Position.Bottom} className="!bg-gray-400" />
    </div>
  );
}

// Leaf: one route's providers in a single box (rule 8) plus its resources.
// `highlighted` marks the any-provider fallback of an expanded visit-type block.
export function RouteNode({ data }) {
  const isBranchDefault = data.highlighted && !data.matched;
  return (
    <div
      className={`rounded-lg bg-white px-3 py-2.5 shadow-sm ${
        data.matched
          ? "border-2 border-emerald-500 shadow-[0_0_0_4px_rgba(16,185,129,0.3)]"
          : isBranchDefault
          ? "border-[3px] border-indigo-500 shadow-[0_0_0_4px_rgba(99,102,241,0.25)]"
          : "border-2 border-gray-300"
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          {data.anyProvider ? (
            <span
              className={`text-[11px] font-semibold ${
                isBranchDefault ? "text-indigo-800" : "italic text-gray-500"
              }`}
            >
              Any provider
            </span>
          ) : (
            <div className="flex items-start gap-1">
              <Users className="mt-0.5 h-3 w-3 shrink-0 text-gray-400" />
              <div className="min-w-0">
                {data.providers.map((provider) => (
                  <div key={provider.id} className="truncate text-[11px] font-semibold text-gray-800">
                    {provider.name}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        <span
          className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold ${
            isBranchDefault ? "bg-indigo-100 text-indigo-800" : "bg-gray-100 text-gray-600"
          }`}
          title="Route position in workflows.json, 1-based (lower wins on overlap)"
        >
          Path {data.routeIndex + 1}
        </span>
      </div>
      {data.matched && (
        <div className="mt-1 text-[9px] font-semibold uppercase tracking-wide text-emerald-700">
          Matched route
        </div>
      )}
      {isBranchDefault && (
        <div className="mt-1.5 inline-block rounded bg-indigo-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
          {data.anyProvider ? "Default for this visit type" : "Fallback for this branch"}
        </div>
      )}
      <ResourceLinks resources={data.resources} />
      <Handle type="target" position={Position.Top} className="!bg-gray-400" />
    </div>
  );
}

// Compact vertical list for branches with many providers (>10). Each row is one
// route: its providers, path label, and resources.
export function ListNode({ data }) {
  // Emphasize the row separators once the list gets long enough to be hard to scan.
  const boldDividers = data.rows.length >= 4;
  return (
    <div className="rounded-lg border-2 border-gray-300 bg-white shadow-sm">
      <div className="border-b border-gray-200 bg-gray-50 px-3 py-1.5 text-[11px] font-bold text-gray-700">
        {data.label} · {data.rows.length} routes
      </div>
      <div
        className={
          boldDividers ? "divide-y-2 divide-gray-300" : "divide-y divide-gray-100"
        }
      >
        {data.rows.map((row) => {
          const isBranchDefault = row.highlighted && !row.matched;
          return (
            <div
              key={row.routeIndex}
              className={`px-3 py-1.5 ${
                row.matched
                  ? "bg-emerald-50 ring-1 ring-inset ring-emerald-400"
                  : isBranchDefault
                  ? "bg-indigo-50 ring-1 ring-inset ring-indigo-400"
                  : ""
              }`}
            >
              <div className="flex items-start justify-between gap-2">
                <span className="min-w-0 text-[11px] font-semibold text-gray-800">
                  {row.anyProvider ? (
                    <span
                      className={
                        isBranchDefault ? "font-semibold text-indigo-800" : "italic text-gray-500"
                      }
                    >
                      Any provider
                    </span>
                  ) : (
                    row.providers.map((provider) => provider.name).join(", ")
                  )}
                </span>
                <span
                  className={`shrink-0 rounded px-1.5 py-0.5 text-[10px] font-bold ${
                    row.matched
                      ? "bg-emerald-100 text-emerald-800"
                      : isBranchDefault
                      ? "bg-indigo-100 text-indigo-800"
                      : "bg-gray-100 text-gray-600"
                  }`}
                  title="Route position in workflows.json, 1-based (lower wins on overlap)"
                >
                  Path {row.routeIndex + 1}
                </span>
              </div>
              {row.matched && (
                <div className="mt-1 text-[9px] font-semibold uppercase tracking-wide text-emerald-700">
                  Matched route
                </div>
              )}
              {isBranchDefault && row.anyProvider && (
                <div className="mt-1 inline-block rounded bg-indigo-600 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-white">
                  Default for this visit type
                </div>
              )}
              <ResourceLinks resources={row.resources} />
            </div>
          );
        })}
      </div>
      <Handle type="target" position={Position.Top} className="!bg-gray-400" />
    </div>
  );
}

export const nodeTypes = {
  specialtyNode: SpecialtyNode,
  defaultNode: DefaultNode,
  groupNode: GroupNode,
  flagNode: FlagNode,
  routeNode: RouteNode,
  listNode: ListNode,
};
